import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Context
import { AppProvider } from './context/AppContext';

// Layout and Common
import { MainLayout } from './components/layout';
import { ErrorBoundary, PageSkeleton } from './components/common';

// Hooks
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';

// Lazy load pages for code splitting
const DashboardHome = lazy(() => import('./components/DashboardHome'));
const GenerationByFuel = lazy(() => import('./components/GenerationByFuel'));
const SystemStatus = lazy(() => import('./components/SystemStatus'));
const PlantList = lazy(() => import('./components/plants/PlantList'));
const BidOfferData = lazy(() => import('./components/plants/BidOfferData'));
const BidOfferAcceptances = lazy(() => import('./components/plants/BidOfferAcceptances'));
const PortfolioMonitor = lazy(() => import('./components/plants/PortfolioMonitor'));
const Analytics = lazy(() => import('./components/Analytics'));
const PlantPerformance = lazy(() => import('./components/analytics/PlantPerformance'));
const MissedOpportunities = lazy(() => import('./components/analytics/MissedOpportunities'));
const ExecutiveSummary = lazy(() => import('./components/analytics/ExecutiveSummary'));
const PortfolioTraderView = lazy(() => import('./components/analytics/PortfolioTraderView'));
const Methodology = lazy(() => import('./components/analytics/Methodology'));
const HistoricalTrends = lazy(() => import('./components/analytics/HistoricalTrends'));

// Auction pages
const AuctionStrategies = lazy(() => import('./components/auction/AuctionStrategies'));
const AuctionAnalytics = lazy(() => import('./components/auction/AuctionAnalytics'));
const CrossBorderView = lazy(() => import('./components/auction/CrossBorderView'));

// Intraday pages
const IntradayPositions = lazy(() => import('./components/intraday/IntradayPositions'));
const IntradayAnalytics = lazy(() => import('./components/intraday/IntradayAnalytics'));

// Settings
const Settings = lazy(() => import('./components/settings/Settings'));
const DataUploadHub = lazy(() => import('./components/settings/DataUploadHub'));

// Market
const SystemPrices = lazy(() => import('./components/market/SystemPrices'));
const InterconnectorFlows = lazy(() => import('./components/market/InterconnectorFlows'));
const EuropeanMarketsOverview = lazy(() => import('./components/market/EuropeanMarketsOverview'));
const BalancingInsights = lazy(() => import('./components/market/BalancingInsights'));

// Platform (pluggable algo trading platform)
const PlatformOverview = lazy(() => import('./components/platform/PlatformOverview'));
const PlatformStrategy = lazy(() => import('./components/platform/StrategyPage'));
const PlatformAlgo = lazy(() => import('./components/platform/AlgoPage'));
const PlatformGrid = lazy(() => import('./components/platform/GridPage'));
const PlatformDispatch = lazy(() => import('./components/platform/DispatchPage'));
const PlatformMarketData = lazy(() => import('./components/platform/MarketDataPage'));
const PlatformPlantDetail = lazy(() => import('./components/platform/PlantDetail'));
const PlatformOrchestrator = lazy(() => import('./components/platform/OrchestratorPage'));
const PlatformHelp = lazy(() => import('./components/platform/HelpPage'));
const PlatformSettings = lazy(() => import('./components/platform/SettingsPage'));

// Observability
const ObsTrace = lazy(() => import('./components/observability/TracePage'));
const ObsEventLog = lazy(() => import('./components/observability/EventLogPage'));
const ObsAdapterHealth = lazy(() => import('./components/observability/AdapterHealthPage'));
const ObsPositionsDb = lazy(() => import('./components/observability/PositionsDbPage'));

// Create dark theme for energy trading application
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00d4ff',
      light: '#5ce1ff',
      dark: '#00a3cc',
    },
    secondary: {
      main: '#00ff88',
      light: '#5cffad',
      dark: '#00cc6a',
    },
    error: {
      main: '#ff6b6b',
    },
    warning: {
      main: '#f1c40f',
    },
    success: {
      main: '#00ff88',
    },
    background: {
      default: '#0a0a1a',
      paper: '#1a1a2e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 600 },
    h2: { fontSize: '2rem', fontWeight: 600 },
    h3: { fontSize: '1.75rem', fontWeight: 600 },
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    body1: { fontSize: '0.938rem' },
    body2: { fontSize: '0.875rem' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#1a1a2e #0a0a1a',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#2a2a4e',
            '&:hover': {
              backgroundColor: '#3a3a5e',
            },
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: '#0a0a1a',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          transition: 'all 0.2s ease',
        },
        contained: {
          background: 'linear-gradient(135deg, #00d4ff 0%, #00a3cc 100%)',
          boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #00e5ff 0%, #00b8e5 100%)',
            boxShadow: '0 6px 20px rgba(0, 212, 255, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: 'rgba(0, 212, 255, 0.5)',
          '&:hover': {
            borderColor: '#00d4ff',
            backgroundColor: 'rgba(0, 212, 255, 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: 'all 0.2s ease',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: '1px solid rgba(255, 255, 255, 0.05)' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(26, 26, 46, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.8rem',
        },
        arrow: {
          color: 'rgba(26, 26, 46, 0.95)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 212, 255, 0.1)',
          },
        },
      },
    },
  },
});

// Placeholder component for pages under development
const ComingSoon = ({ title }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    color: 'white',
    padding: 40,
    textAlign: 'center',
  }}>
    <div style={{
      width: 80,
      height: 80,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 255, 136, 0.1))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 24,
      border: '1px solid rgba(0, 212, 255, 0.2)',
    }}>
      <span style={{ fontSize: 32 }}>🚀</span>
    </div>
    <h2 style={{
      margin: 0,
      marginBottom: 8,
      background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      fontSize: '1.5rem',
      fontWeight: 600,
    }}>{title}</h2>
    <p style={{
      color: 'rgba(255,255,255,0.5)',
      margin: 0,
      fontSize: '0.9rem',
    }}>
      Coming soon — this feature is under development
    </p>
  </div>
);

// Wrapper component that enables keyboard shortcuts
function KeyboardShortcutsProvider({ children }) {
  useKeyboardShortcuts();
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider>
          <Router>
            <KeyboardShortcutsProvider>
              <MainLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {/* Default redirect — Platform is the front door. The legacy
                      market-data dashboard is reachable via the sidebar toggle. */}
                  <Route path="/" element={<Navigate to="/platform" replace />} />

                  {/* Platform — the new pluggable algo platform */}
                  <Route path="/platform" element={<PlatformOverview />} />
                  <Route path="/platform/marketdata" element={<PlatformMarketData />} />
                  <Route path="/platform/strategy" element={<PlatformStrategy />} />
                  <Route path="/platform/algo" element={<PlatformAlgo />} />
                  <Route path="/platform/grid" element={<PlatformGrid />} />
                  <Route path="/platform/dispatch" element={<PlatformDispatch />} />
                  <Route path="/platform/plants/:plantId" element={<PlatformPlantDetail />} />
                  <Route path="/platform/orchestrator" element={<PlatformOrchestrator />} />
                  <Route path="/platform/help" element={<PlatformHelp />} />
                  <Route path="/platform/settings" element={<PlatformSettings />} />

                  {/* Observability */}
                  <Route path="/observability/trace" element={<ObsTrace />} />
                  <Route path="/observability/events" element={<ObsEventLog />} />
                  <Route path="/observability/health" element={<ObsAdapterHealth />} />
                  <Route path="/observability/positions-db" element={<ObsPositionsDb />} />

                  {/* Overview */}
                  <Route path="/dashboard" element={<DashboardHome />} />
                  <Route path="/system" element={<SystemStatus />} />

                  {/* Generation */}
                  <Route path="/generation/fuel" element={<GenerationByFuel />} />
                  <Route path="/generation/plants" element={<PlantList />} />
                  <Route path="/generation/interconnectors" element={<InterconnectorFlows />} />

                  {/* Balancing */}
                  <Route path="/balancing/portfolio" element={<PortfolioMonitor />} />
                  <Route path="/balancing/bod" element={<BidOfferData />} />
                  <Route path="/balancing/boa" element={<BidOfferAcceptances />} />

                  {/* Auction */}
                  <Route path="/auction/strategies" element={<AuctionStrategies />} />
                  <Route path="/auction/analytics" element={<AuctionAnalytics />} />
                  <Route path="/auction/crossborder" element={<CrossBorderView />} />

                  {/* Intraday */}
                  <Route path="/intraday/positions" element={<IntradayPositions />} />
                  <Route path="/intraday/analytics" element={<IntradayAnalytics />} />

                  {/* Market / Data */}
                  <Route path="/market/overview" element={<EuropeanMarketsOverview />} />
                  <Route path="/market/prices" element={<SystemPrices />} />
                  <Route path="/market/balancing" element={<BalancingInsights />} />
                  <Route path="/data/upload" element={<DataUploadHub />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/analytics/summary" element={<ExecutiveSummary />} />
                  <Route path="/analytics/trader" element={<PortfolioTraderView />} />
                  <Route path="/analytics/performance" element={<PlantPerformance />} />
                  <Route path="/analytics/missed" element={<MissedOpportunities />} />
                  <Route path="/analytics/methodology" element={<Methodology />} />
                  <Route path="/analytics/trends" element={<HistoricalTrends />} />

                  {/* Settings */}
                  <Route path="/settings" element={<Settings />} />

                  {/* Legacy routes redirect */}
                  <Route path="/data" element={<Navigate to="/generation/fuel" replace />} />
                </Routes>
              </Suspense>
            </MainLayout>
            </KeyboardShortcutsProvider>
          </Router>
        </AppProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
